import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Box, Paper, Stack, Typography } from '@mui/material'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'

import TextInput from '@/components/ui/inputs/TextInput'
import PageContainer from '@/components/ui/containers/PageContainer'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

type MemberRequestsProps = Readonly<{
  onAccept?: (memberId: string) => void
  onRefuse?: (memberId: string) => void
  pendingMembers: Array<{
    id: string
    isAccepting?: boolean
    isDeclining?: boolean
    fullName: string
  }>
}>

export default function MemberRequests(props: MemberRequestsProps) {
  const { onAccept, onRefuse, pendingMembers } = props
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  return (
    <PageContainer>
      <Stack direction="column" height="100%">
        <Paper square sx={{ position: 'sticky', top: 0 }}>
          {isMobile ? (
            <Stack px={2} pb={2}>
              <Stack pt={3} pb={2}>
                <Typography component="h2" variant="h2">
                  {t('page.network.memberRequests.title')}
                </Typography>
              </Stack>
              <Stack py={1}>
                <TextInput fullWidth />
              </Stack>
            </Stack>
          ) :
            (<Stack px={2} pt={3} pb={2}>
              <Box width="100%" maxWidth="md" px={4}>
                <Stack>
                  <Typography component="h2" variant="h2">
                    {t('page.network.memberRequests.title')}
                  </Typography>
                </Stack>
                <Stack py={1}>
                  <TextInput fullWidth />
                </Stack>
              </Box>
            </Stack>)}
        </Paper>
        <Box flex={1} my={3} px={isMobile ? 0 : 6}>
          {!pendingMembers.length ? (
            <Stack alignItems="center" justifyContent="center" height="100%" spacing={2} marginBottom={5}>
              <PersonSearchRoundedIcon fontSize="large" />
              <Typography component="span" variant="body2">
                {t('page.network.memberRequests.noPendingMember')}
              </Typography>
            </Stack>
          ) : (
            <Stack>
              {pendingMembers.map((pendingMember) => (
                <Paper key={pendingMember.id} elevation={1} sx={{ padding: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>{pendingMember.fullName}</Typography>
                    <Stack direction="row" spacing={isMobile ? 1 : 2}>
                      <LoadingButton
                        loading={pendingMember.isDeclining}
                        onClick={() => onRefuse?.(pendingMember.id)}
                        variant="outlined"
                        color="error"
                      >
                        {t('common:button.refuse.label')}
                      </LoadingButton>
                      <LoadingButton
                        loading={pendingMember.isAccepting}
                        onClick={() => onAccept?.(pendingMember.id)}
                        variant="contained"
                      >
                        {t('common:button.accept.label')}
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </PageContainer>
  )
}
